import _                       from 'underscore';
import classNames               from 'classnames';
import React, { FC, ReactNode } from 'react';
import { StrictCheckboxProps }  from 'semantic-ui-react';
import { useFormikContext }    from 'formik';

import { CorvalAuditEntry, CorvalSubObjective, Position } from '../../../../../types';
import { StyledContainer }                                from '../../../../containers';
import { Checkbox, FieldDescription }                     from '../../../../components/forms/formik';
import { ObjectiveDetailsLink }                           from '../ObjectiveDetailsLink';
import { AuditMemoList }                                  from '../../../../components/audits/AuditMemoList';
import { AUDIT_RECORD_TYPE }                              from '../../../../../constants/SystemRecordEnums';
import { CorvalRecommendedTag }                           from '../../../../components/product/CorvalRecommendedTag';
import { Cell, Grid }                                     from 'styled-css-grid';


export interface ObjectiveSelectionLayoutProps {
    item: Partial<CorvalSubObjective>,
    loading?: boolean,
    disabled?: boolean,
    onChange?: StrictCheckboxProps["onChange"],

    /* Info about the user who last updated the objective state */
    auditRecords?: CorvalAuditEntry[],

    /* The fieldname associated with the pin state toggle */
    fieldValueName?: string,

    /* Flag to display as a visually tigher component */
    compact?: boolean,

    /* Flag to toggle last updated view or not */
    showLastUpdated?: boolean,

    /* Flag to toggle details link view or not */
    includeDetailsLink?: boolean,

    /* Additional content react node */
    additionalContent?: ReactNode,
}

const defaultProps: ObjectiveSelectionLayoutProps = {
    item:               null,
    auditRecords:       null,
    loading:            false,
    disabled:           false,
    fieldValueName:     null,
    compact:            false,
    onChange:           () => {},
    showLastUpdated:    true,
    includeDetailsLink: true,
};

const ObjectiveSelectionLayout: FC<ObjectiveSelectionLayoutProps> = ( props ) => {

    let formik = useFormikContext();

    let { fieldValueName, includeDetailsLink, additionalContent, item, auditRecords, loading, disabled } = props;

    /* If the toggle is active */
    let isActive = formik.values[ props.fieldValueName ];

    let containerClasses = classNames( {
        [ `padding-xs-vertical` ]: props.compact !== false,
        [ `padding-md-vertical` ]: props.compact === false,
    } );

    return (

        <Grid columns={ `1fr auto` } columnGap={ '50px' } className={ containerClasses }>

            <Cell>

                <h3>{ item.displayName } { props.item.isRecommended && (

                    <StyledContainer className={ 'display--inline-block margin-xs-horizontal' } position={ Position.RELATIVE } top={ 3 }>
                        <CorvalRecommendedTag/>
                    </StyledContainer>

                ) }</h3>

                {/* Description */ }
                { item.description && (

                    <FieldDescription>

                        { item.description }

                        { ' ' }

                        { includeDetailsLink && (
                            <ObjectiveDetailsLink
                                id={ item.id }
                                content={ 'View Details' }
                                categoryActivitiesOnly={ isActive }
                                projectDetails={ isActive }
                                onTagClick={ null }
                            />
                        ) }

                    </FieldDescription>
                ) }

                { additionalContent }

                {/* User update information */ }
                { ( !_.isEmpty( auditRecords ) && props.showLastUpdated ) && (

                    <AuditMemoList records={ props.auditRecords }
                                   types={ [ AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_PINSTATE_CHANGED ] }
                                   timeBoundary={ 'newest' }
                                   actionDesc={ 'made the decision to' }
                                   actionVerb={ isActive ? 'include' : 'exclude' }
                                   auditSubject={ 'this objective' }
                                   auditAdditionalInfo={ ( !isActive && props.item.isRecommended ) ? `Corval recommends you include this objective in your project.` : null }
                                   memoPropsByType={ {
                                       // [ AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_PINSTATE_CHANGED ]: {
                                       //     includeAvatar: false,
                                       // }
                                   } }
                                   wrapperComponent={ ( recordProps ) => {

                                       return <>{ recordProps.children }</>;

                                       /*return (
                                        <Grid columns={ `1fr` }>
                                        <Cell>
                                        <LastUpdatedAuditTag { ...recordProps } useAvatar={ true } statement={ ( user, timestamp, statement ) => <span className={ 'text--muted' }>{ recordProps.children }</span> }/>
                                        </Cell>
                                        <Cell>
                                        { ( !isActive && props.item.isRecommended ) && `Corval recommends you include this objective in your project.` }
                                        </Cell>
                                        </Grid>
                                        );*/

                                   } }/>
                ) }

            </Cell>
            <Cell>

                {/* Pin state toggle control */ }
                <Checkbox toggle
                          id={ `bool-${ item.id }` }
                          name={ fieldValueName }
                          disabled={ loading || disabled }
                          onChange={ props.onChange }/>

            </Cell>

        </Grid>
    );
};

ObjectiveSelectionLayout.defaultProps = defaultProps;

export { ObjectiveSelectionLayout }
